import {
  useEffect,
  useState,
  useRef,
  useCallback,
  type MutableRefObject,
  type ReactNode,
} from 'react'
import { cn } from '@/tiptap/lib/tiptap-utils'
import { useUploadFile } from '@/lib/upload'
import { EmojiDropdownMenu } from '@/tiptap/tiptap-ui/emoji-dropdown-menu'
import { EmojiMenu } from '@/tiptap/tiptap-ui/emoji-menu'
import Emoji, { gitHubEmojis, type EmojiItem } from '@tiptap/extension-emoji'
import Image from '@tiptap/extension-image'
import Link from '@/tiptap/tiptap-extensions/common/link-extension'
import Mention from '@tiptap/extension-mention'
import Placeholder from '@tiptap/extension-placeholder'
import { useEditor, EditorContent, BubbleMenu } from '@tiptap/react'
import type { Editor } from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import { useAtom } from 'jotai'
import {
  Bold,
  Italic,
  Strikethrough,
  AtSign,
  ArrowUp,
  X,
  Smile,
  Code,
  Heading2,
  Heading3,
  Image as ImageIcon,
  Link2,
  List,
  ListOrdered,
  Loader2,
  Maximize2,
  Minimize2,
  Paperclip,
  TextQuote,
} from 'lucide-react'
import tippy from 'tippy.js'
import 'tippy.js/dist/tippy.css'
import { workspaceIdAtom } from '@/stores/workspaceStore'
import { Button } from '@deepguide-ai/ui/button'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@deepguide-ai/ui/dialog'
import { Input } from '@deepguide-ai/ui/input'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@deepguide-ai/ui/popover'
import { Separator } from '@deepguide-ai/ui/separator'
import { getAvatarGradient, getAvatarSeed, getUserInitials } from '@deepguide-ai/ui/user-avatar'

export interface CommentEditorSubmitPayload {
  text: string
  json: any
  mentions: string[]
}

export interface CommentEditorProps {
  value?: string
  jsonValue?: any // For rich content JSON
  placeholder?: string
  autoFocus?: boolean
  /** Set false to freeze the editor (e.g. while a parent mutation is
   *  in flight) so late keystrokes aren't silently dropped. */
  editable?: boolean
  /** Accessible name for the contenteditable region. */
  ariaLabel?: string
  minHeightClass?: string
  maxHeightClass?: string
  onSubmit?: (payload: CommentEditorSubmitPayload) => void
  onCancel?: () => void
  members?: Array<{ id: string; name?: string | null; email?: string | null; image?: string | null; isAgent?: boolean }>
  showBorder?: boolean
  showSubmitButton?: boolean
  /** Show the bottom formatting toolbar (bold/italic/strike/mention/emoji). */
  showToolbar?: boolean
  /** Show a floating formatting toolbar on text selection (description surfaces). */
  showBubbleMenu?: boolean
  /** Attachments: paperclip button + paste/drop upload. Images insert
   *  in-place as image nodes; other files insert as links. */
  allowAttachments?: boolean
  /** With `allowAttachments`, lets the parent host the attach trigger
   *  elsewhere (e.g. a dialog footer): the editor assigns an open-picker
   *  callback to this ref and suppresses its built-in paperclip row. */
  attachPickerRef?: MutableRefObject<(() => void) | null>
  /** Fires when attachment uploads start/finish — gate submit on it so a
   *  create can't race a mid-flight upload and drop the file. */
  onUploadingChange?: (uploading: boolean) => void
  /** Show an expand/shrink toggle that enlarges the writing area. */
  allowExpand?: boolean
  /** Round the editor shell corners. */
  rounded?: boolean
  /** Editor background. 'white' matches comments; 'transparent' blends into a parent page. */
  bgVariant?: 'white' | 'transparent'
  /** Extra TipTap extensions appended to the editor — e.g. a scoped SlashCommand. */
  extraExtensions?: any[]
  /** Submit on Cmd/Ctrl+Enter only (defaults to plain Enter for the comment use case). */
  submitOnModEnterOnly?: boolean
  onChangeContent?: (text: string, json: any, mentions: string[]) => void
  editorClassName?: string
  editorDensity?: 'default' | 'compact'
}

function BubbleButton({
  active,
  onClick,
  label,
  children,
}: {
  active?: boolean
  onClick: () => void
  label: string
  children: ReactNode
}) {
  return (
    <Button
      type='button'
      variant={active ? 'default' : 'ghost'}
      size='xs'
      // Keep the text selection alive — a mousedown on the toolbar must
      // not blur the editor and collapse the selection before the click.
      onMouseDown={(e) => e.preventDefault()}
      onClick={onClick}
      aria-label={label}
      aria-pressed={active}
      className={cn(
        'text-muted-foreground h-6 w-6 !rounded-[4px] p-0',
        active &&
          'text-white dark:bg-white/20 dark:text-white dark:hover:bg-white/25'
      )}
    >
      {children}
    </Button>
  )
}

/** Floating formatting toolbar shown on text selection. The Link button is
 *  gated on the `link` mark being registered in the editor schema (it now is,
 *  as a core CommentEditor extension). `onAttach` (set when attachments are
 *  enabled) adds an Image button that opens the same file picker as the
 *  paperclip. */
function SelectionToolbar({
  editor,
  onAttach,
}: {
  editor: Editor
  onAttach?: () => void
}) {
  const supportsLink = Boolean(editor.schema.marks.link)
  const [linkDialogOpen, setLinkDialogOpen] = useState(false)
  const [linkUrl, setLinkUrl] = useState('')

  const openLinkDialog = () => {
    setLinkUrl((editor.getAttributes('link')?.href as string) ?? '')
    setLinkDialogOpen(true)
  }
  const applyLink = () => {
    const raw = linkUrl.trim()
    if (raw) {
      const href = /^[a-z][a-z0-9+.-]*:/i.test(raw) ? raw : `https://${raw}`
      editor
        .chain()
        .focus()
        .extendMarkRange('link')
        .setLink({ href })
        .run()
    }
    setLinkDialogOpen(false)
    setLinkUrl('')
  }
  const removeLink = () => {
    editor.chain().focus().extendMarkRange('link').unsetLink().run()
    setLinkDialogOpen(false)
    setLinkUrl('')
  }

  return (
    <BubbleMenu
      editor={editor}
      tippyOptions={{ moveTransition: 'transform 0.15s ease-out' }}
      shouldShow={({ editor, state }) =>
        editor.isEditable &&
        !state.selection.empty &&
        !editor.isActive('codeBlock')
      }
      className='popover-shadow flex w-fit items-center gap-0.5 rounded-sm border bg-card p-1'
    >
      <BubbleButton
        active={editor.isActive('bold')}
        onClick={() => editor.chain().focus().toggleBold().run()}
        label='Bold'
      >
        <Bold className='h-3.5 w-3.5' />
      </BubbleButton>
      <BubbleButton
        active={editor.isActive('italic')}
        onClick={() => editor.chain().focus().toggleItalic().run()}
        label='Italic'
      >
        <Italic className='h-3.5 w-3.5' />
      </BubbleButton>
      <BubbleButton
        active={editor.isActive('strike')}
        onClick={() => editor.chain().focus().toggleStrike().run()}
        label='Strikethrough'
      >
        <Strikethrough className='h-3.5 w-3.5' />
      </BubbleButton>
      <BubbleButton
        active={editor.isActive('code')}
        onClick={() => editor.chain().focus().toggleCode().run()}
        label='Inline code'
      >
        <Code className='h-3.5 w-3.5' />
      </BubbleButton>
      {supportsLink && (
        <BubbleButton
          active={editor.isActive('link')}
          onClick={openLinkDialog}
          label='Link'
        >
          <Link2 className='h-3.5 w-3.5' />
        </BubbleButton>
      )}
      {onAttach && (
        <BubbleButton onClick={onAttach} label='Insert image or file'>
          <ImageIcon className='h-3.5 w-3.5' />
        </BubbleButton>
      )}
      <Separator orientation='vertical' className='mx-0.5 !h-5' />
      <BubbleButton
        active={editor.isActive('heading', { level: 2 })}
        onClick={() =>
          editor.chain().focus().toggleHeading({ level: 2 }).run()
        }
        label='Heading 2'
      >
        <Heading2 className='h-3.5 w-3.5' />
      </BubbleButton>
      <BubbleButton
        active={editor.isActive('heading', { level: 3 })}
        onClick={() =>
          editor.chain().focus().toggleHeading({ level: 3 }).run()
        }
        label='Heading 3'
      >
        <Heading3 className='h-3.5 w-3.5' />
      </BubbleButton>
      <Separator orientation='vertical' className='mx-0.5 !h-5' />
      <BubbleButton
        active={editor.isActive('bulletList')}
        onClick={() => editor.chain().focus().toggleBulletList().run()}
        label='Bullet list'
      >
        <List className='h-3.5 w-3.5' />
      </BubbleButton>
      <BubbleButton
        active={editor.isActive('orderedList')}
        onClick={() => editor.chain().focus().toggleOrderedList().run()}
        label='Numbered list'
      >
        <ListOrdered className='h-3.5 w-3.5' />
      </BubbleButton>
      <BubbleButton
        active={editor.isActive('blockquote')}
        onClick={() => editor.chain().focus().toggleBlockquote().run()}
        label='Blockquote'
      >
        <TextQuote className='h-3.5 w-3.5' />
      </BubbleButton>
      {supportsLink && (
        <Dialog open={linkDialogOpen} onOpenChange={setLinkDialogOpen}>
          <DialogContent className='sm:max-w-md'>
            <DialogHeader>
              <DialogTitle>Insert link</DialogTitle>
            </DialogHeader>
            <div className='px-6'>
              <Input
                placeholder='https://example.com'
                value={linkUrl}
                onChange={(e) => setLinkUrl(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault()
                    applyLink()
                  }
                }}
                autoFocus
              />
            </div>
            <DialogFooter>
              {editor.isActive('link') && (
                <Button variant='outline' onClick={removeLink}>
                  Remove link
                </Button>
              )}
              <Button onClick={applyLink} disabled={!linkUrl.trim()}>
                Insert
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </BubbleMenu>
  )
}

function collectMentions(node: any, acc: string[] = []): string[] {
  if (!node) return acc
  if (node.type === 'mention' && node.attrs && node.attrs.id) {
    acc.push(String(node.attrs.id))
  }
  const children = (node as any).content
  if (Array.isArray(children)) {
    for (const child of children) collectMentions(child, acc)
  }
  return acc
}

/** True when the doc carries a non-text leaf (an inserted image attachment).
 *  Whitespace-only text is NOT an attachment — `editor.isEmpty` returns
 *  false for a doc of only spaces, so we can't rely on it. */
function hasAttachmentNode(node: any): boolean {
  if (!node) return false
  if (node.type === 'image') return true
  const children = node.content
  return Array.isArray(children) && children.some(hasAttachmentNode)
}

/** Submit payload for both submit paths (Enter key + button), or null when
 *  there's nothing to send. Attachment-only comments have no plain text —
 *  fall back to a label so the `content` column (and its min-length) still
 *  holds. */
function buildSubmitPayload(editor: Editor | null) {
  const json = editor?.getJSON()
  const mentions = collectMentions(json, [])
  const text =
    editor?.getText().trim() ||
    (hasAttachmentNode(json) ? '(attachment)' : '')
  return text ? { text, json, mentions } : null
}

export function CommentEditor({
  value,
  jsonValue,
  placeholder = 'Reply...',
  autoFocus = true,
  editable = true,
  ariaLabel,
  minHeightClass = '',
  maxHeightClass = 'max-h-40',
  onSubmit,
  onCancel,
  members,
  showBorder = true,
  showSubmitButton = true,
  showToolbar = true,
  showBubbleMenu = false,
  allowAttachments = false,
  attachPickerRef,
  onUploadingChange,
  allowExpand = false,
  rounded = true,
  bgVariant = 'white',
  extraExtensions,
  submitOnModEnterOnly = false,
  onChangeContent,
  editorClassName,
  editorDensity = 'default',
}: CommentEditorProps) {
  const [_workspaceId] = useAtom(workspaceIdAtom)
  // Use ref to always access current members in suggestion callback
  const membersRef = useRef(members || [])
  membersRef.current = members || []
  // Track when mention popup is open to prevent Enter/Escape from being hijacked
  const mentionOpenRef = useRef(false)
  const [expanded, setExpanded] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const { uploadFile, isUploading } = useUploadFile({
    fileProps: { category: 'other' },
  })
  // isUploading dips to false between files of a multi-file batch (it's
  // per-call); batchUploading spans the whole handleFiles loop so consumers
  // gating on onUploadingChange don't see a mid-batch "idle" window.
  const [batchUploading, setBatchUploading] = useState(false)
  const uploadBusy = isUploading || batchUploading
  // Paste/drop handlers inside `useEditor` fire before `editor` exists in
  // this closure — route through a ref that always holds the live handler.
  const handleFilesRef = useRef<((files: File[]) => void) | null>(null)

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        hardBreak: {
          keepMarks: true,
        },
      }),
      // Placeholder for empty editor
      Placeholder.configure({
        placeholder,
        emptyNodeClass: 'is-editor-empty',
      }),
      // Emoji support via @tiptap/extension-emoji
      Emoji.configure({
        emojis: gitHubEmojis.filter((e) => !e.name.includes('regional')),
        forceFallbackImages: true,
      }),
      // Typed/pasted URLs auto-link; non-image attachments insert as links.
      Link.configure({
        openOnClick: false,
        HTMLAttributes: { class: 'text-primary underline' },
      }),
      // In-place image attachments (paste/drop/paperclip upload).
      Image.configure({
        HTMLAttributes: { class: 'rounded-[4px] max-w-full' },
      }),
      Mention.configure({
        HTMLAttributes: { class: 'text-primary font-medium' },
        renderLabel: ({ node }) =>
          `@${node.attrs.label || node.attrs.name || node.attrs.email || 'user'}`,
        suggestion: {
          char: '@',
          items: ({ query }: any) => {
            const list = membersRef.current || []
            const q = (query || '').toLowerCase()
            return list
              .filter(
                (m: any) =>
                  (m.name || '').toLowerCase().includes(q) ||
                  (m.email || '').toLowerCase().includes(q)
              )
              .slice(0, 5)
              .map((m: any) => ({
                id: m.id,
                name: m.name,
                email: m.email,
                image: m.image,
                isAgent: m.isAgent,
                label: m.name || m.email,
              }))
          },
          render: () => {
            let popup: any
            let container: HTMLDivElement
            let selectedIndex = 0
            let currentItems: any[] = []
            let currentCommand: any = null

            const renderList = () => {
              if (!container) return
              container.innerHTML = ''
              const wrap = document.createElement('div')
              wrap.className =
                'rounded-md border bg-card p-1 shadow-md min-w-[220px]'
              currentItems.forEach((item: any, index: number) => {
                const btn = document.createElement('button')
                const isSelected = index === selectedIndex
                btn.className = `flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-left text-sm text-foreground !cursor-pointer ${isSelected ? 'bg-accent' : 'hover:bg-accent'}`
                btn.dataset.index = String(index)

                const displayName = item.name || item.email || ''
                const avatar = document.createElement('div')
                if (item.isAgent) {
                  avatar.className =
                    'flex-shrink-0 h-6 w-6 rounded-full bg-primary-subtle text-cobalt-strong ring-1 ring-border flex items-center justify-center dark:text-cobalt-strong'
                  avatar.innerHTML =
                    '<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 8V4H8"/><rect width="16" height="12" x="4" y="8" rx="2"/><path d="M2 14h2"/><path d="M20 14h2"/><path d="M15 13v2"/><path d="M9 13v2"/></svg>'
                } else {
                  avatar.className =
                    'flex-shrink-0 h-6 w-6 rounded-full bg-muted flex items-center justify-center overflow-hidden'
                  const showInitials = () => {
                    avatar.style.background = getAvatarGradient(
                      getAvatarSeed({ name: item.name, email: item.email })
                    )
                    const initial = document.createElement('span')
                    initial.className = 'text-[9px] font-medium text-white'
                    initial.textContent = getUserInitials({
                      name: item.name,
                      email: item.email,
                    })
                    avatar.appendChild(initial)
                  }
                  if (item.image) {
                    const img = document.createElement('img')
                    img.src = item.image
                    img.alt = displayName || 'User'
                    img.className = 'h-full w-full object-cover'
                    img.addEventListener('error', () => {
                      img.remove()
                      showInitials()
                    })
                    avatar.appendChild(img)
                  } else {
                    showInitials()
                  }
                }
                btn.appendChild(avatar)

                // Name text
                const nameSpan = document.createElement('span')
                nameSpan.textContent = item.name || item.email
                btn.appendChild(nameSpan)

                btn.onclick = () => {
                  if (currentCommand) {
                    currentCommand({ id: item.id, label: item.name || item.email })
                  }
                }
                wrap.appendChild(btn)
              })
              container.appendChild(wrap)
            }

            return {
              onStart: (props: any) => {
                mentionOpenRef.current = true
                selectedIndex = 0
                currentItems = props.items || []
                currentCommand = props.command
                container = document.createElement('div') as HTMLDivElement
                popup = tippy('body', {
                  getReferenceClientRect: props.clientRect,
                  appendTo: () => document.body,
                  content: container,
                  showOnCreate: true,
                  interactive: true,
                  placement: 'bottom-start',
                })
                renderList()
              },
              onUpdate: (props: any) => {
                selectedIndex = 0
                currentItems = props.items || []
                currentCommand = props.command
                renderList()
                popup[0].setProps({ getReferenceClientRect: props.clientRect })
              },
              onKeyDown: (props: any) => {
                const { event } = props
                if (currentItems.length === 0) return false
                if (event.key === 'ArrowUp') {
                  selectedIndex = (selectedIndex - 1 + currentItems.length) % currentItems.length
                  renderList()
                  return true
                }
                if (event.key === 'ArrowDown') {
                  selectedIndex = (selectedIndex + 1) % currentItems.length
                  renderList()
                  return true
                }
                if (event.key === 'Enter') {
                  const item = currentItems[selectedIndex]
                  if (item && currentCommand) {
                    currentCommand({ id: item.id, label: item.name || item.email })
                  }
                  return true
                }
                if (event.key === 'Escape') {
                  popup[0].hide()
                  return true
                }
                return false
              },
              onExit: () => {
                mentionOpenRef.current = false
                popup[0].destroy()
              },
            }
          },
        },
      }),
      ...(extraExtensions ?? []),
    ],
    editorProps: {
      attributes: {
        class: `w-full rounded-md border bg-background mx-0 px-3 py-2 text-sm ${minHeightClass} ${maxHeightClass} focus:outline-none`,
        ...(ariaLabel ? { 'aria-label': ariaLabel } : {}),
      },
      handlePaste: (_view, event) => {
        if (!allowAttachments) return false
        const files = Array.from(event.clipboardData?.files ?? [])
        if (files.length === 0) return false
        event.preventDefault()
        handleFilesRef.current?.(files)
        return true
      },
      handleDrop: (_view, event) => {
        if (!allowAttachments) return false
        const files = Array.from(event.dataTransfer?.files ?? [])
        if (files.length === 0) return false
        event.preventDefault()
        handleFilesRef.current?.(files)
        return true
      },
      handleKeyDown: (_view, event) => {
        // Let mention popup handle Enter/Escape when it's open
        if (mentionOpenRef.current) {
          return false
        }
        // Description surfaces only submit on Cmd/Ctrl+Enter — plain
        // Enter inserts a new line so users can write multi-paragraph
        // bodies. Comment surfaces keep the single-Enter submit.
        const submitKey = submitOnModEnterOnly
          ? event.key === 'Enter' && (event.metaKey || event.ctrlKey)
          : event.key === 'Enter' && !event.shiftKey
        if (submitKey) {
          event.preventDefault()
          // Description surfaces (mod+Enter) still submit when the editor is
          // empty — the parent form may be valid on other fields alone.
          const payload =
            buildSubmitPayload(editor) ??
            (submitOnModEnterOnly
              ? { text: '', json: editor?.getJSON(), mentions: [] }
              : null)
          if (payload) {
            onSubmit?.(payload)
            if (!submitOnModEnterOnly) {
              editor?.commands.clearContent(true)
            }
          }
          return true
        }
        if (event.key === 'Escape') {
          onCancel?.()
          return true
        }
        return false
      },
    },
    autofocus: autoFocus ? 'end' : false,
    editable,
    content: jsonValue || value || '',
    onUpdate: ({ editor }) => {
      const text = editor?.getText() || ''
      const json = editor?.getJSON()
      const mentions = collectMentions(json, [])

      onChangeContent?.(text, json, mentions)
    },
  })

  useEffect(() => {
    if (!editor) return
    const contentToSet = jsonValue || value
    if (!contentToSet) return
    // Try to set content when value or jsonValue changes
    try {
      editor.commands.setContent(contentToSet)
    } catch (e) {
      console.error('Error setting content', e)
    }
  }, [editor, value, jsonValue])

  // Clear content when value/jsonValue becomes an empty string or undefined (e.g. after submit)
  useEffect(() => {
    if (!editor) return
    // Only clear when both value and jsonValue are falsy
    if (!value && !jsonValue) {
      editor.commands.clearContent(true)
    }
  }, [editor, value, jsonValue])

  useEffect(() => {
    if (autoFocus) editor?.commands.focus('end')
  }, [editor, autoFocus])

  // Hand the parent an open-picker callback for an externally hosted
  // attach trigger (see attachPickerRef prop).
  useEffect(() => {
    if (!attachPickerRef || !allowAttachments) return
    attachPickerRef.current = () => fileInputRef.current?.click()
    return () => {
      attachPickerRef.current = null
    }
  }, [attachPickerRef, allowAttachments])

  useEffect(() => {
    onUploadingChange?.(uploadBusy)
  }, [uploadBusy, onUploadingChange])

  useEffect(() => {
    if (editor && editor.isEditable !== editable) editor.setEditable(editable)
  }, [editor, editable])

  // Slash-menu "Image" (description surface) fires this custom event — it
  // can't reach the picker ref directly from module-level command defs.
  useEffect(() => {
    if (!editor || !allowAttachments) return
    const open = () => fileInputRef.current?.click()
    // @ts-expect-error - custom event, same pattern as 'insertDocVideo'
    editor.on('openAttachmentPicker', open)
    return () => {
      // @ts-expect-error - custom event
      editor.off('openAttachmentPicker', open)
    }
  }, [editor, allowAttachments])

  // Upload each file, then insert in-place: images become image nodes,
  // everything else a link on the filename (upload.ts surfaces errors).
  const handleFiles = useCallback(
    async (files: File[]) => {
      if (!editor) return
      setBatchUploading(true)
      try {
        for (const file of files) {
          const result = (await uploadFile(file)) as
            | { result?: { data?: { json?: { url?: string } } } }
            | undefined
          const url = result?.result?.data?.json?.url
          if (!url) continue
          if (file.type.startsWith('image/')) {
            // Image is a block node — pair it with a trailing paragraph so
            // there is always an editable line below, and add one above when
            // the image lands as the first node (otherwise the caret can't be
            // placed on either side to keep typing).
            editor
              .chain()
              .focus()
              .insertContent([
                { type: 'image', attrs: { src: url, alt: file.name } },
                { type: 'paragraph' },
              ])
              .run()
            if (editor.state.doc.firstChild?.type.name === 'image') {
              editor.commands.insertContentAt(0, { type: 'paragraph' })
            }
          } else {
            editor
              .chain()
              .focus()
              .insertContent([
                {
                  type: 'text',
                  text: file.name,
                  marks: [{ type: 'link', attrs: { href: url } }],
                },
                { type: 'text', text: ' ' },
              ])
              .run()
          }
        }
      } finally {
        setBatchUploading(false)
      }
    },
    [editor, uploadFile]
  )
  handleFilesRef.current = allowAttachments ? handleFiles : null

  // Toolbar ability state
  const [_, setBump] = useState(0)
  useEffect(() => {
    if (!editor) return
    const rerender = () => setBump((n) => (n + 1) % 1000)
    editor.on('transaction', rerender)
    return () => {
      editor.off('transaction', rerender)
    }
  }, [editor])

  const bgClass =
    bgVariant === 'transparent'
      ? '[&>div]:!bg-transparent'
      : '[&>div]:!bg-card'
  const shellBgClass = bgVariant === 'white' ? 'bg-card' : ''
  const editorDensityClass = expanded
    ? '[&_.ProseMirror]:!min-h-[240px] [&_.ProseMirror]:!max-h-[50vh]'
    : editorDensity === 'compact'
      ? '[&_.ProseMirror]:!min-h-[40px] [&_.ProseMirror]:!max-h-none'
      : '[&_.ProseMirror]:!min-h-[70px] [&_.ProseMirror]:!max-h-[100px]'

  return (
    <div className='w-full space-y-2'>
      <div
        className={cn(
          'w-full overflow-hidden',
          rounded && 'rounded-sm',
          showBorder && 'border',
          shellBgClass
        )}
      >
        {showBubbleMenu && editor && (
          <SelectionToolbar
            editor={editor}
            onAttach={
              allowAttachments
                ? () => fileInputRef.current?.click()
                : undefined
            }
          />
        )}
        <EditorContent
          editor={editor}
          className={cn(
            'w-full [&_.ProseMirror]:!mx-0 [&_.ProseMirror]:!w-full [&_.ProseMirror]:!max-w-none [&>div]:w-full [&>div]:overflow-y-auto [&>div]:border-none',
            editorDensityClass,
            bgClass,
            editorClassName
          )}
        >
          <EmojiDropdownMenu />
        </EditorContent>
        {/* Toolbar-less attachment surfaces (the task description) still get
            a visible attach affordance — a slim multica-style paperclip row.
            Paste/drop works regardless; this makes the capability findable. */}
        {allowAttachments && !showToolbar && (
          <>
            <input
              ref={fileInputRef}
              type='file'
              multiple
              className='hidden'
              onChange={(e) => {
                const files = Array.from(e.target.files ?? [])
                if (files.length > 0) void handleFiles(files)
                e.target.value = ''
              }}
            />
            {/* When the parent hosts its own trigger (attachPickerRef), only
                the hidden input stays mounted. */}
            {!attachPickerRef && (
              <div className='flex items-center gap-0 px-1 py-1'>
                <Button
                  type='button'
                  variant='ghost'
                  size='xs'
                  disabled={uploadBusy}
                  onClick={() => fileInputRef.current?.click()}
                  aria-label='Attach files'
                  title='Attach files or images'
                >
                  {uploadBusy ? (
                    <Loader2 className='text-muted-foreground h-4 w-4 animate-spin' />
                  ) : (
                    <Paperclip className='text-muted-foreground h-4 w-4' />
                  )}
                </Button>
              </div>
            )}
          </>
        )}
        {showToolbar && (
        <div className='flex items-center justify-between gap-2 px-2 py-1.5'>
          <div className='flex items-center gap-0'>
            <Button
              type='button'
              variant='ghost'
              size='xs'
              onClick={() => editor?.chain().focus().insertContent('@').run()}
              aria-label='Mention'
            >
              <AtSign className='text-muted-foreground h-4 w-4' />
            </Button>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  type='button'
                  variant='ghost'
                  size='xs'
                  aria-label='Insert emoji'
                >
                  <Smile className='text-muted-foreground h-4 w-4' />
                </Button>
              </PopoverTrigger>
              <PopoverContent className='w-[350px] border-none p-0'>
                {(() => {
                  const emojis: EmojiItem[] =
                    (editor as any)?.extensionStorage?.emoji?.emojis || []
                  return (
                    <EmojiMenu
                      emojis={emojis}
                      showSearch
                      onSelect={(emoji: EmojiItem) => {
                        editor?.chain().focus().setEmoji(emoji.name).run()
                      }}
                    />
                  )
                })()}
              </PopoverContent>
            </Popover>

            <Separator orientation='vertical' className='mx-1 !h-6' />

            <Button
              type='button'
              variant={editor?.isActive('bold') ? 'default' : 'ghost'}
              size='xs'
              onClick={() => editor?.chain().focus().toggleBold().run()}
              aria-label='Bold'
              aria-pressed={editor?.isActive('bold')}
              className={cn(
                editor?.isActive('bold') &&
                  'dark:bg-white/20 dark:text-white dark:hover:bg-white/25'
              )}
            >
              <Bold
                className={cn(
                  'text-muted-foreground h-4 w-4',
                  editor?.isActive('bold') && 'text-white'
                )}
              />
            </Button>
            <Button
              type='button'
              variant={editor?.isActive('italic') ? 'default' : 'ghost'}
              size='xs'
              onClick={() => editor?.chain().focus().toggleItalic().run()}
              aria-label='Italic'
              aria-pressed={editor?.isActive('italic')}
              className={cn(
                editor?.isActive('italic') &&
                  'dark:bg-white/20 dark:text-white dark:hover:bg-white/25'
              )}
            >
              <Italic
                className={cn(
                  'text-muted-foreground h-4 w-4',
                  editor?.isActive('italic') && 'text-white'
                )}
              />
            </Button>
            <Button
              type='button'
              variant={editor?.isActive('strike') ? 'default' : 'ghost'}
              size='xs'
              onClick={() => editor?.chain().focus().toggleStrike().run()}
              aria-label='Strikethrough'
              aria-pressed={editor?.isActive('strike')}
              className={cn(
                editor?.isActive('strike') &&
                  'dark:bg-white/20 dark:text-white dark:hover:bg-white/25'
              )}
            >
              <Strikethrough
                className={cn(
                  'text-muted-foreground h-4 w-4',
                  editor?.isActive('strike') && 'text-white'
                )}
              />
            </Button>
            {/* Lists were reachable only from the selection bubble menu, which
                needs text to already exist and be selected — no help to anyone
                WRITING a list. An auditor listing what they still need is the
                normal shape of these comments. */}
            <Button
              type='button'
              variant={editor?.isActive('bulletList') ? 'default' : 'ghost'}
              size='xs'
              onClick={() => editor?.chain().focus().toggleBulletList().run()}
              aria-label='Bullet list'
            >
              <List
                className={cn(
                  'text-muted-foreground h-4 w-4',
                  editor?.isActive('bulletList') && 'text-white'
                )}
              />
            </Button>
            <Button
              type='button'
              variant={editor?.isActive('orderedList') ? 'default' : 'ghost'}
              size='xs'
              onClick={() => editor?.chain().focus().toggleOrderedList().run()}
              aria-label='Numbered list'
            >
              <ListOrdered
                className={cn(
                  'text-muted-foreground h-4 w-4',
                  editor?.isActive('orderedList') && 'text-white'
                )}
              />
            </Button>
            {/* 与UI沟通，先注释 */}
            {/* <Button
              type='button'
              variant='ghost'
              size='xs'
              onClick={() => {
                const url = window.prompt('Enter URL')
                if (!url) return
                editor
                  ?.chain()
                  .focus()
                  .extendMarkRange('link')
                  .setLink({ href: url })
                  .run()
              }}
              aria-label='Insert link'
            >
              <LinkIcon className='text-muted-foreground h-4 w-4' />
            </Button> */}
          </div>
          <div className='flex items-center gap-1.5'>
            {allowExpand && (
              <Button
                type='button'
                variant='ghost'
                size='xs'
                onClick={() => setExpanded((prev) => !prev)}
                aria-label={expanded ? 'Shrink editor' : 'Enlarge editor'}
              >
                {expanded ? (
                  <Minimize2 className='text-muted-foreground h-4 w-4' />
                ) : (
                  <Maximize2 className='text-muted-foreground h-4 w-4' />
                )}
              </Button>
            )}
            {allowAttachments && (
              <>
                <input
                  ref={fileInputRef}
                  type='file'
                  multiple
                  className='hidden'
                  onChange={(e) => {
                    const files = Array.from(e.target.files ?? [])
                    if (files.length > 0) void handleFiles(files)
                    e.target.value = ''
                  }}
                />
                <Button
                  type='button'
                  variant='ghost'
                  size='xs'
                  disabled={uploadBusy}
                  onClick={() => fileInputRef.current?.click()}
                  aria-label='Attach files'
                >
                  {uploadBusy ? (
                    <Loader2 className='text-muted-foreground h-4 w-4 animate-spin' />
                  ) : (
                    <Paperclip className='text-muted-foreground h-4 w-4' />
                  )}
                </Button>
              </>
            )}
            {onCancel && (
              <Button
                type='button'
                variant='ghost'
                size='xs'
                onClick={() => onCancel?.()}
                aria-label='Cancel'
              >
                <X className='h-4 w-4' />
              </Button>
            )}
            {showSubmitButton && (
              <Button
                type='button'
                size='xs'
                className='rounded-md'
                disabled={!editor || editor.isEmpty || uploadBusy}
                onClick={() => {
                  const payload = buildSubmitPayload(editor)
                  if (payload) {
                    onSubmit?.(payload)
                    editor?.commands.clearContent(true)
                  }
                }}
                aria-label='Submit'
              >
                <ArrowUp className='h-4 w-4' />
              </Button>
            )}
          </div>
        </div>
        )}
      </div>
    </div>
  )
}
